/**
 * A tenant bought wallet credit. Platform-operator counterpart of the tenant's own
 * `billing_topup_credited` receipt — both are emitted by the same seam, so a top-up is
 * reported to the operator exactly when the tenant is told it landed.
 */

import type { PlatformEventDefinition, PlatformEventPayloadBase } from './definition'
import { BILLING_CHANNELS, type BillingChannel } from '@/lib/billing/constants'
import { formatCentsAsUsd, formatMicroUsd } from '@/lib/billing/utils/format'

export const PLATFORM_TENANT_TOPUP_EVENT_TYPE = 'platform_tenant_topup' as const

export interface PlatformTenantTopupPayload extends PlatformEventPayloadBase {
  /** What the tenant paid, in cents. */
  priceCents: number
  /** What the wallet was credited (price + plan bonus), in microUSD. */
  creditMicrousd: number
  bonusPercent: number
  /** Wallet balance after the credit, in microUSD. */
  balanceMicrousd: number
  channel: BillingChannel
}

const CHANNELS: readonly BillingChannel[] = Object.values(BILLING_CHANNELS)

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value)
}

function isTopupPayload(payload: unknown): payload is PlatformTenantTopupPayload {
  if (typeof payload !== 'object' || payload === null) return false
  const p = payload as Record<string, unknown>
  return (
    typeof p.tenantId === 'string' &&
    typeof p.tenantName === 'string' &&
    isFiniteNumber(p.priceCents) &&
    isFiniteNumber(p.creditMicrousd) &&
    isFiniteNumber(p.bonusPercent) &&
    isFiniteNumber(p.balanceMicrousd) &&
    CHANNELS.includes(p.channel as BillingChannel)
  )
}

export const platformTenantTopupEventDefinition: PlatformEventDefinition<PlatformTenantTopupPayload> =
  {
    eventType: PLATFORM_TENANT_TOPUP_EVENT_TYPE,
    supportedChannelTypes: ['email', 'telegram'],
    validatePayload: isTopupPayload,
    system: {
      label: 'Tenant bought a top-up',
      description: 'A tenant paid for wallet credit — through Stripe or on its Shopify invoice.',
      summarize: (payload) => ({
        title: `${payload.tenantName} topped up ${formatCentsAsUsd(payload.priceCents)}`,
        facts: [
          { label: 'Tenant', value: payload.tenantName },
          { label: 'Paid', value: formatCentsAsUsd(payload.priceCents) },
          { label: 'Credited', value: formatMicroUsd(payload.creditMicrousd) },
          ...(payload.bonusPercent > 0
            ? [{ label: 'Plan bonus', value: `${payload.bonusPercent}%` }]
            : []),
          { label: 'New balance', value: formatMicroUsd(payload.balanceMicrousd) },
          { label: 'Billed via', value: payload.channel },
        ],
        href: '/?page=billing-admin&tab=tenants',
      }),
    },
  }
